import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Phone, Plus, Search, ArrowLeft, Building2, AlertTriangle, 
  X, Edit2, Trash2, ExternalLink, MapPin
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { emergencyNumbersAPI } from '../services/api';

interface EmergencyNumber {
  id: string;
  name: string;
  description: string;
  address?: string;
  phone: string;
  category: 'emergencia' | 'seguridad' | 'servicios' | 'salud' | 'otros';
  icon?: string;
}

// Mock data removed — page fetches real data from emergencyNumbersAPI

export default function NumerosEmergencia() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [numbers, setNumbers] = useState<EmergencyNumber[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState<'all' | EmergencyNumber['category']>('all');
  const [showNewModal, setShowNewModal] = useState(false);
  const [editingNumber, setEditingNumber] = useState<EmergencyNumber | null>(null);

  const { isAdmin } = useAuth();

  const condoId = (() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}').condoId; }
    catch { return undefined; }
  })();

  useEffect(() => {
    loadNumbers();
  }, []);

  const loadNumbers = async () => {
    setLoading(true);
    try {
      const userData = (() => {
        try { return JSON.parse(localStorage.getItem('user') || '{}'); }
        catch { return {}; }
      })();
      const condoId = userData.condoId;
      if (!condoId) {
        setNumbers([]);
        setLoading(false);
        return;
      }
      const response = await emergencyNumbersAPI.getAll(condoId);
      const raw = response?.data ?? response;
      const list = Array.isArray(raw) ? raw : (Array.isArray(raw?.items) ? raw.items : []);
      setNumbers(list);
    } catch (err) {
      console.error('Error loading emergency numbers:', err);
      setNumbers([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredNumbers = numbers.filter(n => {
    if (filter !== 'all' && n.category !== filter) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        n.name.toLowerCase().includes(query) ||
        n.description.toLowerCase().includes(query) ||
        n.phone.includes(query)
      );
    }
    return true;
  });

  const handleCall = (phone: string) => {
    window.location.href = `tel:${phone.replace(/-/g, '')}`;
  };

  const handleDelete = async (id: string) => {
    if (!confirm('¿Eliminar este número?')) return;
    try {
      await emergencyNumbersAPI.remove(id);
      setNumbers(prev => prev.filter(n => n.id !== id));
    } catch (err) {
      console.error('Error deleting emergency number:', err);
      alert('No se pudo eliminar el número');
    }
  };

  const getCategoryLabel = (category: string) => {
    const labels: Record<string, string> = {
      emergencia: 'Emergencia',
      seguridad: 'Seguridad',
      servicios: 'Servicios',
      salud: 'Salud',
      otros: 'Otros'
    };
    return labels[category] || category;
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'emergencia': return 'bg-red-100 text-red-700';
      case 'seguridad': return 'bg-blue-100 text-blue-700';
      case 'servicios': return 'bg-amber-100 text-amber-700';
      case 'salud': return 'bg-emerald-100 text-emerald-700';
      default: return 'bg-[var(--v-surface-2)] text-[var(--v-text-primary)]';
    }
  };

  if (!condoId) {
    return (
      <div className="min-h-full bg-[var(--v-base-bg)] flex items-center justify-center" style={{ fontFamily: 'Inter, sans-serif' }}>
        <div className="text-center">
          <div className="w-16 h-16 bg-[var(--v-surface-2)] rounded-2xl flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-8 h-8 text-[var(--v-text-tertiary)]" />
          </div>
          <h2 className="text-xl font-bold text-[var(--v-text-primary)] mb-2">Sin condominio asignado</h2>
          <p className="text-[var(--v-text-secondary)]">Aún no se te ha asignado un condominio. Contacta al administrador para continuar.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-[var(--v-base-bg)]" style={{ fontFamily: 'Inter, sans-serif' }}>
      <header className="sticky top-0 bg-[var(--v-surface)] border-b border-[var(--v-border)] z-50">
        <div className="flex items-center justify-between h-16 px-6">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/home/dashboard')} className="p-2 text-[var(--v-text-primary)] hover:bg-[var(--v-surface-2)] rounded-xl">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-[var(--v-text-primary)]">Números de Emergencia</h1>
              <p className="text-xs text-[var(--v-text-secondary)]">Contactos importantes del condominio</p>
            </div>
          </div>
          {isAdmin() && (
            <button 
              onClick={() => setShowNewModal(true)}
              className="px-4 py-2 bg-[#36CCD8] text-white font-bold rounded-xl hover:bg-[#2DB8C4] flex items-center gap-2"
            >
              <Plus className="w-4 h-4" /> Agregar
            </button>
          )}
        </div>
      </header>

      <main className="p-6 max-w-4xl mx-auto">
        {/* Emergency Banner */}
        <div className="bg-red-50 border border-red-200 rounded-2xl p-6 mb-6">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 bg-red-500 rounded-2xl flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-7 h-7 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-red-700 mb-1">¿Emergencia?</h2>
              <p className="text-red-600 mb-3">En caso de emergencia vital, llama inmediatamente al:</p>
              <button 
                onClick={() => handleCall('911')}
                className="px-6 py-3 bg-red-500 text-white font-bold text-xl rounded-xl hover:bg-red-600 flex items-center gap-3"
              >
                <Phone className="w-6 h-6" /> 911
              </button>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-[var(--v-surface)] rounded-2xl border border-[var(--v-border)] shadow-sm p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--v-text-tertiary)]" />
              <input
                type="text"
                placeholder="Buscar número o servicio..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-[var(--v-surface-2)] border border-[var(--v-border)] rounded-xl focus:border-[#36CCD8] focus:outline-none"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              {(['all', 'emergencia', 'seguridad', 'servicios', 'salud', 'otros'] as const).map((cat) => (
                <button
                  key={cat}
                  onClick={() => setFilter(cat)}
                  className={`px-4 py-2 rounded-xl font-medium text-sm transition-all ${
                    filter === cat
                      ? 'bg-[#36CCD8] text-white'
                      : 'bg-[var(--v-surface-2)] text-[var(--v-text-secondary)] hover:bg-[var(--v-surface-3)]'
                  }`}
                >
                  {cat === 'all' ? 'Todos' : getCategoryLabel(cat)}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Numbers List */}
        <div className="grid md:grid-cols-2 gap-4">
          {filteredNumbers.map(number => (
            <div key={number.id} className="bg-[var(--v-surface)] rounded-2xl border border-[var(--v-border)] shadow-sm p-5 hover:shadow-md transition-all">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#36CCD8]/10 rounded-xl flex items-center justify-center text-2xl">
                  {number.icon || '📞'}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-bold text-white">{number.name}</h3>
                    <span className={`px-2 py-0.5 text-xs font-bold rounded-full ${getCategoryColor(number.category)}`}>
                      {getCategoryLabel(number.category)}
                    </span>
                  </div>
                  <p className="text-sm text-[var(--v-text-secondary)] mb-2">{number.description}</p>
                  {number.address && (
                    <p className="text-xs text-[var(--v-text-tertiary)] flex items-center gap-1 mb-2">
                      <MapPin className="w-3 h-3" /> {number.address}
                    </p>
                  )}
                  <div className="flex items-center gap-2">
                    <button 
                      onClick={() => handleCall(number.phone)}
                      className="flex-1 py-2 bg-[#36CCD8] text-white font-bold rounded-xl hover:bg-[#2DB8C4] flex items-center justify-center gap-2"
                    >
                      <Phone className="w-4 h-4" /> {number.phone}
                    </button>
                    {isAdmin() && (
                      <>
                        <button 
                          onClick={() => setEditingNumber(number)}
                          className="p-2 text-[var(--v-text-tertiary)] hover:text-[#36CCD8] hover:bg-[#36CCD8]/10 rounded-xl"
                        >
                          <Edit2 className="w-5 h-5" />
                        </button>
                        <button 
                          onClick={() => handleDelete(number.id)}
                          className="p-2 text-[var(--v-text-tertiary)] hover:text-red-500 hover:bg-red-50 rounded-xl"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredNumbers.length === 0 && (
          <div className="text-center py-12 text-[var(--v-text-secondary)]">
            <Phone className="w-12 h-12 mx-auto mb-3 text-[var(--v-text-tertiary)]" />
            <p>No se encontraron números</p>
          </div>
        )}
      </main>

      {/* New/Edit Modal */}
      {(showNewModal || editingNumber) && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-[var(--v-surface)] rounded-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-white mb-6">
              {editingNumber ? 'Editar Número' : 'Nuevo Número de Emergencia'}
            </h3>
            <form className="space-y-4">
              <div>
                <label className="block text-sm font-bold text-[var(--v-text-primary)] mb-2">Nombre</label>
                <input 
                  type="text" 
                  defaultValue={editingNumber?.name}
                  className="w-full px-4 py-3 bg-[var(--v-surface-2)] border border-[var(--v-border)] rounded-xl focus:border-[#36CCD8] focus:outline-none" 
                  placeholder="Ej: Seguridad 24h"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-[var(--v-text-primary)] mb-2">Categoría</label>
                <select className="w-full px-4 py-3 bg-[var(--v-surface-2)] border border-[var(--v-border)] rounded-xl focus:border-[#36CCD8] focus:outline-none">
                  <option value="emergencia">Emergencia</option>
                  <option value="seguridad">Seguridad</option>
                  <option value="servicios">Servicios</option>
                  <option value="salud">Salud</option>
                  <option value="otros">Otros</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-bold text-[var(--v-text-primary)] mb-2">Teléfono</label>
                <input 
                  type="tel" 
                  defaultValue={editingNumber?.phone}
                  className="w-full px-4 py-3 bg-[var(--v-surface-2)] border border-[var(--v-border)] rounded-xl focus:border-[#36CCD8] focus:outline-none" 
                  placeholder="555-0000"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-[var(--v-text-primary)] mb-2">Descripción</label>
                <input 
                  type="text" 
                  defaultValue={editingNumber?.description}
                  className="w-full px-4 py-3 bg-[var(--v-surface-2)] border border-[var(--v-border)] rounded-xl focus:border-[#36CCD8] focus:outline-none" 
                  placeholder="Breve descripción del servicio"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-[var(--v-text-primary)] mb-2">Dirección (opcional)</label>
                <input 
                  type="text" 
                  defaultValue={editingNumber?.address}
                  className="w-full px-4 py-3 bg-[var(--v-surface-2)] border border-[var(--v-border)] rounded-xl focus:border-[#36CCD8] focus:outline-none" 
                  placeholder="Ubicación dentro del condominio"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button 
                  type="button"
                  onClick={() => { setShowNewModal(false); setEditingNumber(null); }}
                  className="flex-1 py-3 border border-[var(--v-border)] text-[var(--v-text-primary)] font-bold rounded-xl hover:bg-[var(--v-surface-2)]"
                >
                  Cancelar
                </button>
                <button 
                  type="button"
                  onClick={() => { setShowNewModal(false); setEditingNumber(null); alert('✅ Guardado'); }}
                  className="flex-1 py-3 bg-[#36CCD8] text-white font-bold rounded-xl hover:bg-[#2DB8C4]"
                >
                  Guardar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
